import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  IconButton,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
} from '@mui/material';
import {
  GitHub,
  LinkedIn,
  Twitter,
  Email,
  Phone,
  LocationOn,
  Send,
  Support,
  Business,
} from '@mui/icons-material';

const ContactSection: React.FC = () => {
  return (
    <Box sx={{ py: 12, bgcolor: '#0f0f23' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="Contact"
            sx={{
              bgcolor: 'rgba(102, 126, 234, 0.2)',
              color: '#667eea',
              fontWeight: 600,
              mb: 2,
              border: '1px solid rgba(102, 126, 234, 0.3)',
            }}
          />
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: 'white',
            }}
          >
            Get in Touch
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Have questions or need support? We're here to help you navigate your metro journey.
          </Typography>
        </Box>

        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                bgcolor: '#1a1a2e',
                overflow: 'visible',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 4 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      mb: 3,
                    }}
                  >
                    <Support />
                  </Avatar>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    Let's Connect
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      lineHeight: 1.6,
                      mb: 4,
                    }}
                  >
                    Our team is ready to assist you with any questions about MetroRahi, 
                    technical support, or partnership opportunities.
                  </Typography>
                </Box>

                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: '#4CAF50',
                        mr: 3,
                      }}
                    >
                      <Email />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                        Email Support
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        support@metrorahi.com
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: '#FF9800',
                        mr: 3,
                      }}
                    >
                      <Phone />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                        Phone Support
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        +1 (555) 123-4567
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: '#E91E63',
                        mr: 3,
                      }}
                    >
                      <LocationOn />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                        Office Location
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        MetroRahi HQ, Tech City
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Social Media & Quick Actions */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                bgcolor: '#1a1a2e',
                overflow: 'visible',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 4 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      mb: 3,
                    }}
                  >
                    <Business />
                  </Avatar>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    Follow & Connect
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      lineHeight: 1.6,
                      mb: 4,
                    }}
                  >
                    Stay updated with the latest features, announcements, and metro insights.
                  </Typography>
                </Box>

                {/* Social Media Buttons */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                    Social Media
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<GitHub />}
                      sx={{
                        borderColor: '#333',
                        color: '#333',
                        borderRadius: 3,
                        px: 3,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#333',
                          bgcolor: '#333',
                          color: 'white',
                        },
                      }}
                      component="a"
                      href="https://github.com/metrorahi"
                      target="_blank"
                    >
                      GitHub
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<LinkedIn />}
                      sx={{
                        borderColor: '#0077B5',
                        color: '#0077B5',
                        borderRadius: 3,
                        px: 3,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#0077B5',
                          bgcolor: '#0077B5',
                          color: 'white',
                        },
                      }}
                      component="a"
                      href="https://linkedin.com/company/metrorahi"
                      target="_blank"
                    >
                      LinkedIn
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Twitter />}
                      sx={{
                        borderColor: '#1DA1F2',
                        color: '#1DA1F2',
                        borderRadius: 3,
                        px: 3,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#1DA1F2',
                          bgcolor: '#1DA1F2',
                          color: 'white',
                        },
                      }}
                      component="a"
                      href="https://twitter.com/metrorahi"
                      target="_blank"
                    >
                      Twitter
                    </Button>
                  </Stack>
                </Box>

                {/* Quick Actions */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                    Quick Actions
                  </Typography>
                  <Stack spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<Send />}
                      sx={{
                        bgcolor: '#667eea',
                        borderRadius: 3,
                        py: 1.5,
                        '&:hover': {
                          bgcolor: '#5a6fd8',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                      component="a"
                      href="mailto:support@metrorahi.com"
                    >
                      Send Message
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: '#667eea',
                        color: '#667eea',
                        borderRadius: 3,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#5a6fd8',
                          bgcolor: '#667eea',
                          color: 'white',
                        },
                      }}
                      component="a"
                      href="/admin/login"
                    >
                      Admin Access
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: 'white',
            }}
          >
            Ready to get started?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#667eea',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: '#5a6fd8',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
              component="a"
              href="mailto:support@metrorahi.com"
            >
              Contact Us
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#5a6fd8',
                  bgcolor: '#667eea',
                  color: 'white',
                },
                transition: 'all 0.3s ease',
              }}
              component="a"
              href="/admin/login"
            >
              Admin Panel
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactSection; 